import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivationEnd, Event, NavigationEnd, Router } from '@angular/router';
import { ModalsService } from '@core/modals/modals.service';
import { OktaIntegrationService } from '@core/okta-integration/services/okta-integration.service';
import { DvmService, TutorialService } from '@core/services';
import { TransactionService } from '@core/services/transaction.service';
import { PrivateSellingService } from '@core/services/private-selling.service';
import { IsolatedSeatsService } from '@features/digital-venue/services/isolated-seats.service';
import { ConnectionService } from '@features/pages/select-seat/services/connection.service';
import { UpgradesComponent } from '@features/pages/select-seat/viewer/topbar/upgrades/upgrades.component';
import { UpgradesService } from '@features/pages/select-seat/viewer/topbar/upgrades/upgrades.service';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { SaleEvent } from '../../models';
import { ConfigurationService } from '../../services/configuration.service';

@Component({
  imports: [CommonModule, NgOptimizedImage, UpgradesComponent],
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  // Booleans
  landing: boolean;
  showLegend: boolean;
  showTutorialButton = false;

  // Strings
  actualPage: string;
  // 'summary' is the final confirmation page (no further review step to go back to), so it never
  // gets a back button — only 'checkout' does.
  backBtn: 'checkout' | undefined;
  patronName: string | undefined;
  /**
   * Second line of the centred "Welcome, {name}" block (events header, node
   * 1490:4). Populated from the same `OktaIntegrationService.getUserInfo()`
   * call as `patronName` — no separate lookup, and left `undefined` (not a
   * fabricated placeholder) whenever Okta doesn't return one.
   */
  patronEmail: string | undefined;

  /**
   * Signed-in patron block ("Hi, {name}" + Sign out) shown in the header on
   * checkout/summary. Only checkout has an Okta login concept today (the inline
   * `app-okta-container` "Login to use account credit" widget), so this
   * mirrors that same OktaIntegrationService state instead of inventing a
   * separate one; it stays hidden for guests (no Okta session) rather than
   * showing fabricated data.
   */
  showUserInfo = false;
  isAuthenticated = false;

  // Colors
  availableColor: string;
  mySeatsColor: string;
  selectedColor: string;
  isolatedColor: string;

  // Event
  event: SaleEvent;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  private readonly DATE_FORMAT = 'EEEE, LLL d, y, h:mm a';
  // Split out for the seat-selection header, which
  // interleaves a clock glyph between the weekday and the time — a single
  // preformatted string can't express that, so the date is rendered as
  // three separate parts instead. `DATE_FORMAT`/`formattedEventDate` above
  // are kept as-is for the `title` tooltip.
  private readonly WEEKDAY_FORMAT = 'EEEE';
  private readonly TIME_FORMAT = 'h:mm a';
  private readonly MONTH_DAY_FORMAT = 'LLL d';

  router = inject(Router);
  isolatedSeatsService = inject(IsolatedSeatsService);
  configurationService = inject(ConfigurationService);
  // Gates `<app-upgrades>` below: it should only mount when the current
  // event actually has upgrades to show.
  protected upgradesService: UpgradesService = inject(UpgradesService);
  private connectionService = inject(ConnectionService);
  private dvmService = inject(DvmService);
  private modalsService = inject(ModalsService);
  private tutorialService = inject(TutorialService);
  private transactionService = inject(TransactionService);
  private oktaIntegrationService = inject(OktaIntegrationService);
  private privateSellingService = inject(PrivateSellingService);

  // Getters
  get isTopview() {
    return this.dvmService.isTopView();
  }
  get isSectionMap() {
    return !this.dvmService.isTopView();
  }
  get is3dOpen() {
    return this.dvmService.is3dView();
  }
  get isIsolatedActive() {
    return this.isolatedSeatsService.isolatedSeatsActive;
  }
  get eventDateTime(): DateTime | null {
    if (!this.event?.date) {
      return null;
    }
    const hasTimezone =
      this.event.date.includes('Z') || /[+-]\d{2}:\d{2}$/.test(this.event.date);
    if (hasTimezone) {
      return DateTime.fromISO(this.event.date);
    }
    return DateTime.fromISO(this.event.date, { zone: 'utc' });
  }
  get eventTimeZone(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.offsetNameShort || '';
  }
  get formattedEventDate(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.setLocale('en').toFormat(this.DATE_FORMAT);
  }
  // Weekday / time / month-day parts of the same event date, split out for
  // the seat-selection header layout. Same guard as `formattedEventDate`.
  get eventWeekday(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.setLocale('en').toFormat(this.WEEKDAY_FORMAT);
  }
  get eventTimeOfDay(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.setLocale('en').toFormat(this.TIME_FORMAT);
  }
  get eventMonthDay(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.setLocale('en').toFormat(this.MONTH_DAY_FORMAT);
  }
  /**
   * Gates the info icon + Sign out pill on the `events` header: shown only
   * when the visit is a group link that requires private selling.
   * `PrivateSellingService.isEnabled` is set
   * by `EventDataResolver` whenever the *current* seat-selection event has
   * `isPrivateSellingEnabled` — it's an app-wide signal (not reset back to
   * `false` on navigation), so it also reads correctly if the patron comes
   * back to `events` mid-visit. `events` itself never touches this signal
   * (its resolver is `eventLandingResolver`/`opponentsResolver`, not
   * `EventDataResolver`), so this can only ever flip on because a
   * private-selling event was already reached via `seat-selection` in this
   * same visit. Still requires `isAuthenticated` so a guest never sees a
   * "Sign out" for a session that isn't theirs.
   */
  get showPrivateSellingActions(): boolean {
    return (
      this.landing &&
      this.isAuthenticated &&
      this.privateSellingService.isEnabled()
    );
  }
  /**
   * Gates the header band's background — checkout and summary have no
   * distinct header band; the band is painted the page's own grey instead.
   * Deliberately a separate getter from `showUserInfo`:
   * that flag is about whether the patron block renders, this one is about
   * background colour, and they happen to share the same route check today
   * but are conceptually different — keeping them separate avoids a future
   * change to one silently affecting the other.
   */
  get isCartRoute(): boolean {
    return this.actualPage === 'checkout' || this.actualPage === 'summary';
  }

  constructor() {
    this.mySeatsColor = 'red';
    this.selectedColor = 'purple';
    this.isolatedColor = 'orange';
  }

  ngOnInit(): void {
    this.initComponent();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }

  private initComponent() {
    const routerEventSubscription = this.router.events.subscribe(
      (event: Event) => {
        if (event instanceof NavigationEnd) {
          this.showLegend = event.url.includes('seat-selection');
          this.showTutorialButton =
            event.url.includes('seat-selection') ||
            event.url.includes('checkout');

          this.landing = event.url.includes('events');
        }

        if (event instanceof ActivationEnd && event.snapshot.url.length) {
          this.actualPage = event.snapshot.url[0].path;
          this.backBtn =
            this.actualPage === 'checkout' ? 'checkout' : undefined;

          this.showUserInfo =
            this.actualPage === 'checkout' || this.actualPage === 'summary';
        }
      }
    );

    const getEventSubscription = this.connectionService
      .getEvent()
      .subscribe(data => (this.event = data));

    /**
     * Checkout/summary never emit through `connectionService.getEvent()` above (that subject is
     * only fed by the seat-selection flow), so a refresh, OKTA return, or deep link straight into
     * `/checkout/:id` or `/summary/:id` left `event` unset and the header showed no event/date.
     * `transaction$` is populated immediately in both components' `ngOnInit` from the resolver's
     * payload, so it's a reliable source on direct loads too.
     */
    const transactionEventSubscription =
      this.transactionService.transaction$.subscribe(wrapper => {
        const event = wrapper?.transaction?.event;
        if (event) {
          this.event = event;
        }
      });

    /**
     * A persistent subscription rather than a one-shot check on route
     * change: the header stays mounted for the whole app (it isn't
     * recreated per page), and in demo mode signing in via checkout's
     * inline `app-okta-container` doesn't trigger any navigation/route
     * event for the header to react to. Subscribing to the live
     * OktaIntegrationService state instead — the same source
     * `app-okta-container` reads — picks that up immediately, on checkout
     * or summary, in demo mode and in a real Okta session alike.
     */
    const authStateSubscription = this.oktaIntegrationService
      .getIsAuthenticated()
      .subscribe({
        next: (isAuthenticated: boolean) =>
          void this.onAuthStateChange(isAuthenticated),
        error: (error: unknown) => console.error(error),
      });

    this.subscriptions.push(
      routerEventSubscription,
      getEventSubscription,
      transactionEventSubscription,
      authStateSubscription
    );
  }

  private async onAuthStateChange(isAuthenticated: boolean): Promise<void> {
    this.isAuthenticated = isAuthenticated;
    if (!isAuthenticated) {
      this.patronName = undefined;
      this.patronEmail = undefined;
      return;
    }

    const patron = await this.oktaIntegrationService.getUserInfo();
    this.patronName = patron?.name ?? 'Patron name not found';
    // No fallback string here (unlike patronName above): an absent email is
    // simply not rendered (see the `@if (patronEmail)` in the template)
    // rather than shown as fabricated text pretending to be an address.
    this.patronEmail = patron?.email;
  }

  signOut(): void {
    // `isAuthenticated`/`patronName` reset themselves via the persistent
    // getIsAuthenticated() subscription above once the service flips its
    // state — no need to duplicate that here.
    this.oktaIntegrationService.signOut().subscribe({
      error: (error: unknown) => console.error(error),
    });
  }

  /**
   * Starts the Okta redirect login — the entry point that replaced the
   * checkout card's inline `app-okta-container` login link.
   * `window.location.href` as the origin brings the
   * patron back to the page they were on.
   */
  signIn(): void {
    this.oktaIntegrationService
      .signInWithRedirect(window.location.href, 'okta/redirect')
      .subscribe({ error: (error: unknown) => console.error(error) });
  }

  // Only ever called for `backBtn === 'checkout'` (see the template) —
  // 'summary' has no back button, so there's no other page to branch on here.
  goBack() {
    this.transactionService.restartTransaction();
  }

  goBackToTopview() {
    if (this.dvmService.is3dView()) {
      // Close 3D
      this.dvmService.close3dView();
    } else {
      const backSub = this.dvmService.openSectionMap().subscribe();
      this.subscriptions.push(backSub);
    }
  }

  openLegend() {
    const dynamicLegend = this.isTopview
      ? { text: 'Selected', color: this.selectedColor }
      : { text: 'Isolated', color: this.isolatedColor };

    const modalData = {
      title: 'Location Legend',
      // The mobile-only contact block below is the same copy
      // `total.component.html`'s footer contact line shows on desktop —
      // that element is `hidden lg:block`, so mobile users see it here in
      // the legend modal instead of not at all. Tailwind scans `.ts` files
      // (`@source "./**/*.{html,ts}"` in styles.css), so `lg:hidden` inside
      // this template string still gets its utility classes generated.
      content: `<span id="legend">
                  <span class="mb-4 flex justify-center">
                    <span class="mx-2 flex items-center">
                      <span class="w-2 h-2 mx-2 bg-dvm-available rounded-full"></span>
                      <p>Available</p>
                    </span>
                    <span class="mx-2 flex items-center">
                      <span class="w-2 h-2 mx-2 bg-${this.mySeatsColor} rounded-full"></span>
                      <p>My seats</p>
                    </span>
                    <span class="mx-2 flex items-center">
                      <span class="w-2 h-2 mx-2 bg-${dynamicLegend.color} rounded-full"></span>
                      <p>${dynamicLegend.text}</p>
                    </span>
                  </span>
                </span>
                <span class="lg:hidden mt-4 block border-gray-200 border-t pt-4 text-center">
                  <p class="m-0">For questions or assistance:</p>
                  <p class="m-0">
                    Please call <b class="font-extrabold">${this.configurationService.configuration.phone}</b>
                    or email <b class="font-extrabold">${this.configurationService.configuration.email}</b>
                  </p>
                </span>`,
      acceptBtnName: 'CLOSE',
    };
    this.modalsService.openModal(modalData);
  }

  // Tutorial
  openTutorialQuestion() {
    this.tutorialService.openTutorial();
  }

  isNotFoundRoute() {
    return this.router.url === '/not-found';
  }
}
